import { useMutation } from '@apollo/client';
import { UseFormReturn } from 'react-hook-form';
import { Article } from 't2-graphql-types';
import { updatePromptMutation } from './mutations/updatePromptMutation';
import { showNotification } from '../NotificationBanner/showNotification';
import { TPromptFormFields } from '../PromptForm/types';
import { PromptForm } from '../PromptForm/PromptForm';
import { getSortedPromptsQuery } from '../Prompt/queries/getSortedPromptsQuery';
import { updateLivePromptMutation } from './mutations/updateLivePromptMutation';
import { PromptSettingsLiveStatus } from '../Prompt/constants';
import { getLivePromptVariables } from './getLivePromptVariables';

type TEditPromptFormProps = {
  closeModal: () => void;
  useFormReturn: UseFormReturn<TPromptFormFields>;
  promptData: Article;
};

export const EditPromptForm = ({ closeModal, useFormReturn, promptData }: TEditPromptFormProps) => {
  const [updatePromptRequest] = useMutation(updatePromptMutation, {
    refetchQueries: [getSortedPromptsQuery],
  });
  const [updateLivePromptRequest] = useMutation(updateLivePromptMutation, {
    refetchQueries: [getSortedPromptsQuery],
  });
  const { promptSettings } = promptData;

  const onSubmit = async ({ title, description, startDate, endDate }: TPromptFormFields) => {
    try {
      const variables = {
        title,
        description,
        promptId: promptData?.id,
        ...getLivePromptVariables({ promptSettings, startDate, endDate }),
      };

      if (promptSettings) {
        await updateLivePromptRequest({ variables });
      } else {
        await updatePromptRequest({ variables });
      }

      showNotification({
        type: 'success',
        message: 'Your prompt has been successfully saved.',
      });

      closeModal();
      return;
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Something went wrong. Please try again.',
      });
    }

    closeModal();
  };

  return (
    <PromptForm
      onSubmit={onSubmit}
      submitButtonLabel="Save"
      submitButtonClassName="min-w-[160px] flex justify-center"
      useFormReturn={useFormReturn}
      promptSettingsLiveStatus={promptSettings?.liveStatus as PromptSettingsLiveStatus}
    />
  );
};
